import { nanoid } from '@reduxjs/toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TemplateState,
  NfcFeature,
  LoyaltyBalance,
  QrFeature,
  AppLinkingState,
  LocationInput,
  BeaconInput,
  BeaconUIState,
  AppleImage,
  GoogleImage,
  FieldSection,
  TextField,
  Platform,
  ImageField,
  TemplateType,
  ColorsState,
  Image,
  FieldsState,
  NotificationMessage as NotificationMessageType,
  AppleEnhancedDesignSnapshot,
  EnhancedDesignType,
  FeaturedAction,
  FeaturedActionSlotKey,
} from '../../types/template';
import {
  FIELD_CONFIG,
  UNEDITED_HEADER_LABELS,
  BALANCE_FIELD,
  SCAFFOLD_HEADER_LABEL,
  SCAFFOLD_DEFAULT_VALUE,
} from '../../../components/client/pass-templates-new/utils/fieldVisibilityConfig';
import {
  STAMP_IMAGE_FIELDS,
  STAMP_SHORT_VALUE_KEYS,
  STAMP_LONG_VALUE_KEYS,
} from '../../../components/client/pass-templates-new/utils/stampCardConstants';
import {
  buildInitialFields,
  adjustFieldsForTemplateType,
} from '../../../components/client/pass-templates-new/utils/fieldHelpers';
import { cleanImagesByTemplateType } from '../../../components/client/pass-templates-new/utils/imageHelpers';
import { allowedDesignsForTemplateType } from '../../../components/client/pass-templates-new/utils/enhancedDesignEligibility';
import {
  createEmptyFeaturedAction,
  createEmptyFeaturedActions,
} from '../../../components/client/pass-templates-new/utils/featuredActionTypes';
import { GOOGLE_DEFAULT_WAVE_BANNER_BASE64 } from '../../../utils/base64';
import { initialFormState } from './form.slice';

const enableBalanceField = (state: TemplateState) => {
  if (
    state.fields.header?.one?.id === 'header_one' &&
    UNEDITED_HEADER_LABELS.includes(state.fields.header.one.label)
  ) {
    state.fields.header.one.id = BALANCE_FIELD.id;
    state.fields.header.one.label = BALANCE_FIELD.label;
  }
  const headerDefault = String(state.fields.header?.one?.defaultValue ?? '');
  if (headerDefault !== '' && isNaN(Number(headerDefault))) {
    state.fields.header.one.defaultValue = '';
  }
};

const disableBalanceField = (state: TemplateState) => {
  if (
    state.fields.header?.one?.id === BALANCE_FIELD.id &&
    state.fields.header.one.label === BALANCE_FIELD.label
  ) {
    state.fields.header.one.id = 'header_one';
    state.fields.header.one.label = SCAFFOLD_HEADER_LABEL;
    if (state.fields.header.one.defaultValue === '') {
      state.fields.header.one.defaultValue = SCAFFOLD_DEFAULT_VALUE;
    }
  }
};

const initialState: TemplateState = {
  templateName: '',
  uuid: '',
  status: undefined,
  templateType: 'loyalty',
  description: '',
  centralTitle: '',
  defaultLanguage: 'en',
  transitType: null,
  focusedField: null,
  previewLanguage: null,
  _isCreating: false,
  _formCreateSuccessId: null,
  _formWasDisabledOnLoad: false,
  _fromPreset: false,
  _presetId: null,
  _passesCount: 0,
  _originalNfc: {
    enabled: false,
    source: 'uuid',
    customValue: '',
    googleNfc: false,
  },
  _originalBarcode: {
    enabled: false,
    type: 'qr',
    source: 'uuid',
    value: '',
    displayText: false,
  },
  _originalWatermarks: {
    shared: { color: 'light', position: 'bottomRight' },
    apple: { color: 'light', position: 'bottomRight' },
    google: { color: 'light', position: 'bottomRight' },
  },
  _overrideNfc: false,
  _overrideBarcode: false,
  _overrideFeaturedActions: false,
  chosenGoogleLogo: 'icon',
  images: {
    shared: {
      logo: '',
      icon: '',
      banner: '',
      watermarkColor: 'light',
      watermarkPosition: 'bottomRight',
    },
    apple: {
      logo: '',
      icon: '',
      banner: '',
      thumbnail: '',
      watermarkColor: 'light',
      watermarkPosition: 'bottomRight',
    },
    google: {
      logo: '',
      wideLogo: '',
      banner: '',
      watermarkColor: 'light',
      watermarkPosition: 'bottomRight',
    },
  },
  colors: {
    passBackgroundColor: '#D9D9D9',
    titleFontColor: '#000000',
    bodyFontColor: '#000000',
  },
  fields: buildInitialFields('loyalty'),
  originalDefaultValues: buildInitialFields('loyalty'),
  loyaltyBalance: {
    enabled: false,
    numberType: 'integer',
  },
  nfc: {
    enabled: false,
    source: 'uuid',
    customValue: '',
    googleNfc: false,
  },
  barcode: {
    enabled: false,
    type: 'qr',
    source: 'uuid',
    value: '',
    displayText: false,
  },
  appLinking: {
    appleAppStoreUrl: [''],
    googleDisplayText: '',
    googleAppPackageName: '',
    googleAppPlayStoreUrl: '',
    googleWebUrl: '',
  },
  notificationHeader: '',
  locations: [],
  beacons: [],
  dynamicForm: initialFormState,
  // stamp card
  stampCount: 2,
  stampFeedbackTypes: [],
  notificationMessages: [],
  stampShortKey: '',
  stampLongKey: '',
  stampShortValues: {
    stampZeroShortValue: '',
    stampOneShortValue: '',
    stampTwoShortValue: '',
  },
  stampLongValues: {
    stampZeroLongValue: '',
    stampOneLongValue: '',
    stampTwoLongValue: '',
  },
  updateBannerOnStamp: false,
  appleEnhancedDesign: {
    type: null,
    images: {
      artwork: '',
      secondaryLogo: '',
      venueMap: '',
    },
    artworkBrightness: null,
    featuredActions: createEmptyFeaturedActions(),
  },
  _appleEnhancedDesignOnLoad: {
    type: null,
    images: { artwork: '', secondaryLogo: '', venueMap: '' },
    featuredActions: createEmptyFeaturedActions(),
  },
};

const syncStampValuesToCount = (
  state: TemplateState,
  requiredCount: number
) => {
  const shortKeys = STAMP_SHORT_VALUE_KEYS.slice(0, requiredCount);
  const longKeys = STAMP_LONG_VALUE_KEYS.slice(0, requiredCount);

  const newShortValues: Record<string, string> = {};
  shortKeys.forEach((key) => {
    newShortValues[key] = state.stampShortValues[key] ?? '';
  });
  state.stampShortValues = newShortValues;

  const newLongValues: Record<string, string> = {};
  longKeys.forEach((key) => {
    newLongValues[key] = state.stampLongValues[key] ?? '';
  });
  state.stampLongValues = newLongValues;
};

const clearStampBannerImagesAboveCount = (
  state: TemplateState,
  requiredCount: number
) => {
  const fieldsToClear = STAMP_IMAGE_FIELDS.slice(requiredCount) as ImageField[];
  (['shared', 'apple', 'google'] as Platform[]).forEach((platform) => {
    fieldsToClear.forEach((field) => {
      (state.images[platform] as Record<string, string>)[field] = '';
    });
  });
};

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    setSimpleField: <K extends keyof TemplateState>(
      state: TemplateState,
      action: PayloadAction<{ field: K; value: TemplateState[K] }>
    ) => {
      const { field, value } = action.payload;
      state[field] = value;

      if (field === 'stampFeedbackTypes') {
        const types = value as TemplateState['stampFeedbackTypes'];
        if (!types?.includes('updatePassText')) {
          state.stampShortKey = '';
          state.stampLongKey = '';
          Object.keys(state.stampShortValues).forEach(
            (key) =>
              (state.stampShortValues[
                key as keyof typeof state.stampShortValues
              ] = '')
          );
          Object.keys(state.stampLongValues).forEach(
            (key) =>
              (state.stampLongValues[
                key as keyof typeof state.stampLongValues
              ] = '')
          );
        }
        if (!types?.includes('sendPushNotification')) {
          state.notificationMessages = [];
        }
      }

      if (field === 'stampCount') {
        const numericStampCount = Math.min(Math.max(Number(value) || 2, 2), 12);
        const requiredCount = numericStampCount + 1;
        const current = state.notificationMessages;

        if (current.length < requiredCount) {
          for (let i = current.length; i < requiredCount; i++) {
            state.notificationMessages.push({
              id: nanoid(),
              label: '',
              slot: '',
            });
          }
        } else if (current.length > requiredCount) {
          state.notificationMessages = current.slice(0, requiredCount);
        }

        syncStampValuesToCount(state, requiredCount);
        clearStampBannerImagesAboveCount(state, requiredCount);
      }
    },
    setTemplateType: (
      state,
      action: PayloadAction<{
        newType: TemplateType;
        watermarkEnabled: boolean;
        autoToggleLoyaltyBalance?: boolean;
      }>
    ) => {
      const { newType, watermarkEnabled, autoToggleLoyaltyBalance } =
        action.payload;
      const previousType = state.templateType;
      state.templateType = newType;
      state.fields = adjustFieldsForTemplateType(state.fields, newType);
      state.images = cleanImagesByTemplateType(
        state.images,
        newType,
        watermarkEnabled
      );

      if (autoToggleLoyaltyBalance && newType !== 'loyalty') {
        state.loyaltyBalance.enabled = false;
        disableBalanceField(state);
      }

      if (newType === 'stampCard') {
        STAMP_IMAGE_FIELDS.forEach((field) => {
          (state.images.shared as Record<string, string>)[field] =
            state.images.shared.banner || '';
        });
      }

      // Clear Apple and Google banners when switching between template types
      state.images.apple.banner = '';
      state.images.google.banner = '';

      if (newType === 'generic') {
        state.images.google.banner = GOOGLE_DEFAULT_WAVE_BANNER_BASE64;
      }

      if (newType === 'transit') {
        state.transitType = 'airplane';
      } else {
        state.transitType = null;
      }

      if (state.appleEnhancedDesign.type !== null) {
        const allowedDesigns = allowedDesignsForTemplateType(newType);
        if (!allowedDesigns.includes(state.appleEnhancedDesign.type)) {
          state.appleEnhancedDesign.type = allowedDesigns[0] ?? null;
        }
      }
    },
    setFocusedField: (state, action: PayloadAction<string | null>) => {
      state.focusedField = action.payload;
    },
    setPreviewLanguage: (state, action: PayloadAction<string | null>) => {
      state.previewLanguage = action.payload;
    },
    setTemplate: (state, action: PayloadAction<Partial<TemplateState>>) => {
      Object.assign(state, action.payload);
    },
    setOriginalDefaultValues: (state, action: PayloadAction<FieldsState>) => {
      state.originalDefaultValues = action.payload;
    },
    resetTemplate: () => initialState,

    // design
    setDesignImage: (
      state,
      action: PayloadAction<{
        platform: Platform;
        field: ImageField;
        value: string;
      }>
    ) => {
      const { platform, field, value } = action.payload;
      state.images[platform][field] = value;
      if (platform === 'shared' && field === 'stampZeroImage') {
        state.images.shared.banner = value;
      }
      if (platform === 'shared' && field === 'banner' && value) {
        if (state.images.google.banner === GOOGLE_DEFAULT_WAVE_BANNER_BASE64) {
          state.images.google.banner = '';
        }
      }
    },

    removeDesignImage: (
      state,
      action: PayloadAction<{
        platform: Platform;
        field: ImageField;
      }>
    ) => {
      const { platform, field } = action.payload;
      state.images[platform][field] = null as any;
    },

    setDesignColor: (
      state,
      action: PayloadAction<{
        field: keyof ColorsState;
        value: string;
      }>
    ) => {
      state.colors[action.payload.field] = action.payload.value;
    },

    setWatermarkProp: (
      state,
      action: PayloadAction<{
        platform: Platform;
        field: keyof Image;
        value: string;
      }>
    ) => {
      state.images[action.payload.platform][action.payload.field] =
        action.payload.value;
    },

    setFieldProp: (
      state,
      action: PayloadAction<{
        section: FieldSection;
        slot: string;
        key: keyof TextField | '_newlyAdded' | '_isLink' | 'overrideFieldValue';
        value: string | boolean;
      }>
    ) => {
      const { section, slot, key, value } = action.payload;
      if (!state.fields[section][slot]) {
        state.fields[section][slot] = { id: '', label: '', defaultValue: '' };
      }
      state.fields[section][slot][key] = value as never;
    },
    addField: (
      state,
      action: PayloadAction<{ section: FieldSection; idPrefix?: string }>
    ) => {
      const { section, idPrefix } = action.payload;
      const templateType = state.templateType;
      const limit = FIELD_CONFIG[templateType][section].limit;

      const slotKeys = ['one', 'two', 'three', 'four', 'five', 'six'];
      const existingSlots = Object.keys(state.fields[section]);

      if (existingSlots.length > limit) {
        console.warn(
          `Cannot add more ${section} fields — limit of ${limit} reached for ${templateType}`
        );
        return;
      }

      const availableSlot = slotKeys.find(
        (key) => !existingSlots.includes(key)
      );
      if (!availableSlot) {
        console.warn(`No available slot key left for ${section}`);
        return;
      }

      const prefix = idPrefix || section;
      state.fields[section][availableSlot] = {
        id: `${prefix}_${availableSlot}`,
        label: '',
        defaultValue: '',
        _newlyAdded: true,
      };

      const sortedFields = Object.fromEntries(
        slotKeys
          .filter((key) => state.fields[section][key])
          .map((key) => [key, state.fields[section][key]])
      );
      state.fields[section] = sortedFields;
    },

    reorderField: (
      state,
      action: PayloadAction<{
        section: FieldSection;
        fromIndex: number;
        toIndex: number;
      }>
    ) => {
      const { section, fromIndex, toIndex } = action.payload;
      if (fromIndex === toIndex) return;

      const slotOrder = ['one', 'two', 'three', 'four', 'five', 'six'];
      const activeSlots = slotOrder.filter(
        (s) => state.fields[section][s] != null
      );
      if (
        fromIndex < 0 ||
        fromIndex >= activeSlots.length ||
        toIndex < 0 ||
        toIndex >= activeSlots.length
      )
        return;

      const [movedSlot] = activeSlots.splice(fromIndex, 1);
      activeSlots.splice(toIndex, 0, movedSlot);

      const oldData: Record<string, any> = {};
      activeSlots.forEach((slot) => {
        oldData[slot] = state.fields[section][slot];
      });

      // Clear all slots then write reordered data to avoid stale entries
      const slotMapping: Record<string, string> = {};
      slotOrder.forEach((slot) => {
        delete state.fields[section][slot];
      });
      activeSlots.forEach((originalSlot, i) => {
        const newSlot = slotOrder[i];
        state.fields[section][newSlot] = oldData[originalSlot];
        slotMapping[`${section}_${originalSlot}`] = `${section}_${newSlot}`;
      });

      // Reorder originalDefaultValues to keep override comparisons correct
      if (state.originalDefaultValues[section]) {
        const oldOriginals: Record<string, any> = {};
        activeSlots.forEach((slot) => {
          oldOriginals[slot] = state.originalDefaultValues[section][slot];
        });
        slotOrder.forEach((slot) => {
          delete state.originalDefaultValues[section][slot];
        });
        activeSlots.forEach((originalSlot, i) => {
          const newSlot = slotOrder[i];
          state.originalDefaultValues[section][newSlot] =
            oldOriginals[originalSlot];
        });
      }

      if (slotMapping[state.stampShortKey]) {
        state.stampShortKey = slotMapping[state.stampShortKey];
      }
      if (slotMapping[state.stampLongKey]) {
        state.stampLongKey = slotMapping[state.stampLongKey];
      }
    },

    removeField: (
      state,
      action: PayloadAction<{ section: FieldSection; slot: string }>
    ) => {
      const { section, slot } = action.payload;
      const sectionSlot = `${section}_${slot}`;
      if (sectionSlot === state.stampShortKey) {
        state.stampShortKey = '';
        Object.keys(state.stampShortValues).forEach(
          (key) => (state.stampShortValues[key] = '')
        );
      }
      if (sectionSlot === state.stampLongKey) {
        state.stampLongKey = '';
        Object.keys(state.stampLongValues).forEach(
          (key) => (state.stampLongValues[key] = '')
        );
      }
      delete state.fields[section][slot];
    },
    setNfcFeature: (state, action: PayloadAction<Partial<NfcFeature>>) => {
      if (action.payload.enabled === false) {
        state.nfc = {
          enabled: false,
          source: 'uuid',
          customValue: '',
          googleNfc: state.nfc.googleNfc,
        };
      } else {
        state.nfc = {
          ...state.nfc,
          ...action.payload,
        };
      }
    },
    setBarcodeFeature: (state, action: PayloadAction<Partial<QrFeature>>) => {
      if (action.payload.enabled === false) {
        state.barcode = {
          enabled: false,
          value: '',
          displayText: false,
          type: 'qr',
          source: 'uuid',
        };
      } else {
        state.barcode = {
          ...state.barcode,
          ...action.payload,
        };
      }
    },
    setAppLinkingFeature: (
      state,
      action: PayloadAction<Partial<AppLinkingState>>
    ) => {
      state.appLinking = {
        ...state.appLinking,
        ...action.payload,
      };
    },
    setAppleAppStoreUrl: (
      state,
      action: PayloadAction<{ index: number; value: string }>
    ) => {
      state.appLinking.appleAppStoreUrl[action.payload.index] =
        action.payload.value;
    },

    addAppleAppStoreUrl: (state) => {
      state.appLinking.appleAppStoreUrl.push('');
    },

    removeAppleAppStoreUrl: (state, action: PayloadAction<number>) => {
      state.appLinking.appleAppStoreUrl.splice(action.payload, 1);
    },
    setNotificationHeader: (state, action: PayloadAction<string>) => {
      state.notificationHeader = action.payload;
    },
    setNotificationMessageProp: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof NotificationMessageType;
        value: string;
      }>
    ) => {
      const { index, field, value } = action.payload;
      state.notificationMessages[index][field] = value as never;
    },
    addNotificationMessage: (state) => {
      state.notificationMessages.push({
        id: nanoid(),
        label: '',
        slot: '',
      });
    },
    clearAllNotificationMessages: (state) => {
      state.notificationMessages.forEach((msg) => {
        msg.label = '';
        msg.slot = '';
      });
    },
    syncNotificationMessagesToStampCount: (state) => {
      const numericStampCount = Math.min(
        Math.max(Number(state.stampCount) || 2, 2),
        12
      );
      const requiredCount = numericStampCount + 1;
      const current = state.notificationMessages;

      if (current.length < requiredCount) {
        for (let i = current.length; i < requiredCount; i++) {
          state.notificationMessages.push({
            id: nanoid(),
            label: '',
            slot: '',
          });
        }
      } else if (current.length > requiredCount) {
        state.notificationMessages = current.slice(0, requiredCount);
      }
    },
    syncStampValuesToStampCount: (state) => {
      const numericStampCount = Math.min(
        Math.max(Number(state.stampCount) || 2, 2),
        12
      );
      const requiredCount = numericStampCount + 1;
      syncStampValuesToCount(state, requiredCount);
    },
    setStampShortValue: (
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.stampShortValues) {
        state.stampShortValues[key] = value;
      }
    },
    setStampLongValue: (
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.stampLongValues) {
        state.stampLongValues[key] = value;
      }
    },
    clearStampShortValues: (state) => {
      Object.keys(state.stampShortValues).forEach(
        (key) => (state.stampShortValues[key] = '')
      );
    },
    clearStampLongValues: (state) => {
      Object.keys(state.stampLongValues).forEach(
        (key) => (state.stampLongValues[key] = '')
      );
    },
    clearStampBannerImages: (state) => {
      const platforms: Platform[] = ['shared', 'apple', 'google'];
      platforms.forEach((platform) => {
        STAMP_IMAGE_FIELDS.forEach((field) => {
          (state.images[platform] as Record<string, string>)[field] = '';
        });
      });
    },
    clearStampImagesAndSetBannerToPlaceholder: (state) => {
      state.images.shared.banner = '';
      const platforms: Platform[] = ['shared', 'apple', 'google'];
      platforms.forEach((platform) => {
        STAMP_IMAGE_FIELDS.forEach((field) => {
          (state.images[platform] as Record<string, string>)[field] = '';
        });
      });
    },
    addLocation: (
      state,
      action: PayloadAction<{
        latitude: number;
        longitude: number;
        address?: string;
      }>
    ) => {
      if (!state.locations) state.locations = [];
      if (state.locations.length >= 10) return;

      state.locations.push({
        _id: nanoid(),
        name: '',
        address: action.payload.address ?? '',
        latitude: action.payload.latitude,
        longitude: action.payload.longitude,
        radius: 80,
        message: '',
        _newlyAdded: true,
      });
    },
    removeLocation: (state, action: PayloadAction<number>) => {
      state.locations.splice(action.payload, 1);
    },
    addLanguage: (state, action: PayloadAction<string>) => {
      const languageCode = action.payload;
      const sections: FieldSection[] = ['header', 'central', 'details', 'back'];

      sections.forEach((section) => {
        const fields = state.fields[section];
        Object.keys(fields).forEach((slot) => {
          const field = fields[slot];
          if (!field) return;
          if (!field.localisation) {
            field.localisation = {};
          }
          if (!field.localisation[languageCode]) {
            field.localisation[languageCode] = {
              label: '',
              defaultValue: '',
            };
          }
        });
      });
    },
    removeLanguage: (state, action: PayloadAction<string>) => {
      const languageCode = action.payload;
      const sections: FieldSection[] = ['header', 'central', 'details', 'back'];

      sections.forEach((section) => {
        const fields = state.fields[section];
        Object.keys(fields).forEach((slot) => {
          const field = fields[slot];
          if (!field) return;
          if (field.localisation && field.localisation[languageCode]) {
            delete field.localisation[languageCode];
            if (Object.keys(field.localisation).length === 0) {
              field.localisation = {};
            }
          }
        });
      });
    },
    setFieldLocalisation: (
      state,
      action: PayloadAction<{
        section: FieldSection;
        field: string;
        language: string;
        label?: string;
        defaultValue?: string;
      }>
    ) => {
      const { section, field, language, label, defaultValue } = action.payload;
      const targetField = state.fields[section][field];

      if (!targetField) {
        console.warn(`Field ${field} not found in section ${section}`);
        return;
      }
      if (!targetField.localisation) {
        targetField.localisation = {};
      }
      if (!targetField.localisation[language]) {
        targetField.localisation[language] = {
          label: '',
          defaultValue: '',
        };
      }
      if (label !== undefined) {
        targetField.localisation[language].label = label;
      }
      if (defaultValue !== undefined) {
        targetField.localisation[language].defaultValue = defaultValue;
      }
    },
    setDefaultLanguage: (state, action: PayloadAction<string>) => {
      state.defaultLanguage = action.payload;
    },
    setLocationProp: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof LocationInput;
        value: string | number | boolean;
      }>
    ) => {
      const { index, field, value } = action.payload;
      if (!state.locations[index]) return;

      state.locations[index][field] = value as never;
    },
    addBeacon: (state) => {
      if (state.beacons.length >= 10) return;
      const newBeacon: BeaconUIState = {
        _id: nanoid(),
        name: '',
        proximityUuid: '',
        major: null,
        minor: null,
        message: '',
        _newlyAdded: true,
      };
      state.beacons.push(newBeacon);
    },
    removeBeacon: (state, action: PayloadAction<number>) => {
      state.beacons.splice(action.payload, 1);
    },
    setBeaconProp: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof BeaconInput;
        value: string | number | boolean;
      }>
    ) => {
      const { index, field, value } = action.payload;
      if (!state.beacons[index]) return;

      state.beacons[index][field] = value as never;
    },
    setNotificationHeaderDefault: (
      state,
      action: PayloadAction<{ entityName: string }>
    ) => {
      state.notificationHeader = action.payload.entityName;
      state.centralTitle = action.payload.entityName;
    },
    setLoyaltyBalance: (
      state,
      action: PayloadAction<Partial<LoyaltyBalance>>
    ) => {
      if (action.payload.enabled === true) {
        enableBalanceField(state);
      } else if (action.payload.enabled === false) {
        disableBalanceField(state);
      }
      state.loyaltyBalance = {
        ...state.loyaltyBalance,
        ...action.payload,
      };
    },
    setIsCreating: (state, action: PayloadAction<boolean>) => {
      state._isCreating = action.payload;
    },
    setFormCreateSuccessId: (state, action: PayloadAction<string | null>) => {
      state._formCreateSuccessId = action.payload;
    },
    setFormWasDisabledOnLoad: (state, action: PayloadAction<boolean>) => {
      state._formWasDisabledOnLoad = action.payload;
    },
    setFromPreset: (state, action: PayloadAction<boolean>) => {
      state._fromPreset = action.payload;
    },
    setPresetId: (state, action: PayloadAction<string | null>) => {
      state._presetId = action.payload;
    },
    setAppleEnhancedDesignType: (
      state,
      action: PayloadAction<EnhancedDesignType | null>
    ) => {
      state.appleEnhancedDesign.type = action.payload;
    },
    setAppleEnhancedDesignImage: (
      state,
      action: PayloadAction<{
        field: 'artwork' | 'secondaryLogo' | 'venueMap';
        value: string;
      }>
    ) => {
      state.appleEnhancedDesign.images[action.payload.field] =
        action.payload.value;
      if (action.payload.field === 'artwork') {
        state.appleEnhancedDesign.artworkBrightness = null;
      }
    },
    setAppleEnhancedDesignOnLoad: (
      state,
      action: PayloadAction<AppleEnhancedDesignSnapshot>
    ) => {
      state._appleEnhancedDesignOnLoad = action.payload;
    },
    setFeaturedAction: (
      state,
      action: PayloadAction<{
        slot: FeaturedActionSlotKey;
        value: Partial<FeaturedAction>;
      }>
    ) => {
      const { slot, value } = action.payload;
      state.appleEnhancedDesign.featuredActions[slot] = {
        ...state.appleEnhancedDesign.featuredActions[slot],
        ...value,
      };
    },
    clearFeaturedAction: (
      state,
      action: PayloadAction<FeaturedActionSlotKey>
    ) => {
      state.appleEnhancedDesign.featuredActions[action.payload] =
        createEmptyFeaturedAction();
    },
    swapFeaturedActions: (state) => {
      const { one, two } = state.appleEnhancedDesign.featuredActions;
      state.appleEnhancedDesign.featuredActions.one = two;
      state.appleEnhancedDesign.featuredActions.two = one;
    },
  },
});

export const {
  setTemplateType,
  setSimpleField,
  setDesignImage,
  removeDesignImage,
  setDesignColor,
  setWatermarkProp,
  setFieldProp,
  addField,
  reorderField,
  removeField,
  setFocusedField,
  setPreviewLanguage,
  setNfcFeature,
  setBarcodeFeature,
  setAppLinkingFeature,
  setAppleAppStoreUrl,
  addAppleAppStoreUrl,
  removeAppleAppStoreUrl,
  addLocation,
  removeLocation,
  addLanguage,
  removeLanguage,
  setFieldLocalisation,
  setLocationProp,
  setDefaultLanguage,
  addBeacon,
  removeBeacon,
  setBeaconProp,
  setNotificationHeaderDefault,
  setLoyaltyBalance,
  resetTemplate,
  setIsCreating,
  setFormCreateSuccessId,
  setFormWasDisabledOnLoad,
  setFromPreset,
  setPresetId,
  setTemplate,
  setOriginalDefaultValues,
  setNotificationMessageProp,
  addNotificationMessage,
  clearAllNotificationMessages,
  syncNotificationMessagesToStampCount,
  syncStampValuesToStampCount,
  setStampShortValue,
  setStampLongValue,
  clearStampShortValues,
  clearStampLongValues,
  clearStampBannerImages,
  clearStampImagesAndSetBannerToPlaceholder,
  setAppleEnhancedDesignType,
  setAppleEnhancedDesignImage,
  setAppleEnhancedDesignOnLoad,
  setFeaturedAction,
  clearFeaturedAction,
  swapFeaturedActions,
} = templateSlice.actions;

export default templateSlice.reducer;
